import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { COURSE_TYPES_PATH, DESSERT_DAYS_PATH, menuPath, MODEL_COMPLEX_PATH, SIDE_DAYS_PATH, userNtfNewMenuPath } from '../../../functions/src/admin/paths';
import { getDayAndMonth, getWeekArraysOverlappingCalendarMonth, getWeekdayName, toDateFromServer, toDayString } from '../../../functions/src/api/dates';
import { buildMonthlyMenuMonthDates, getMonthlyMenuExtraMonths } from '../../../functions/src/common/monthlyMenuConfig';
import { ModelComplexity } from '../../../functions/src/types';
import { getCalendarMonthPrintDateRange, monthlyMenuPrintSupportDataFromSnapshots, useMonthlyMenuPrintSupportSnapshots, usePublishedMenuDataForPrintMonth } from '../../admin/menu/monthlyMenuPrintData';
import { EmptyMonthlyMenuError, printMonthlyMenu } from '../../admin/menu/printMonthlyMenu';
import AllergenPdfDownloadLink from '../AllergenPdfDownloadLink';
import { auth, currentMenu, currentSchoolKey, DataSnapshot } from '../../api/firebase';
import { useDatabase, useDatabaseVal, useList } from '../../api/firebaseHooks';
import { getLangValue, langContext } from '../../api/Lang';
import OkDlg from '../../api/OkDlg';
import { useSetDayMenuRangeForDate } from '../../api/useSetDayMenuRangesList';
import { useStrings } from '../../common-strings';
import NewMenuBanner from '../../components/NewMenuBanner';
import PrintIcon from '../../PrintIcon';
import { Meal } from '../../types';
import { useMonthlyMenuStrings } from './monthlyMenu-strings';

type MonthRange = { firstDayString: string; lastDayString: string };

function getMonthMenuRange(date: Date): MonthRange {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDayString: toDayString(firstDay), lastDayString: toDayString(lastDayOfMonth) };
}

function menuKeyInMonthRange(key: string, range: MonthRange): boolean {
    return key >= range.firstDayString && key <= range.lastDayString;
}

/** One query for all candidate months; returns whether each month has menu days. */
function useMenusExistForMonths(monthDates: Date[]): boolean[] {
    const monthRanges = useMemo(() => monthDates.map(getMonthMenuRange), [monthDates]);
    const queryRange = useMemo(() => {
        if (!monthRanges.length) {
            return null;
        }
        return {
            startAt: monthRanges[0].firstDayString,
            endAt: monthRanges[monthRanges.length - 1].lastDayString,
        };
    }, [monthRanges]);

    const menuFilter = useMemo(
        () => (queryRange
            ? (ref) => ref.orderByKey().startAt(queryRange.startAt).endAt(queryRange.endAt)
            : (ref) => ref),
        [queryRange?.startAt, queryRange?.endAt]
    );

    const menuSnapshot = useDatabase(queryRange ? menuPath(currentMenu) : '', menuFilter, true);
    const menuKeys = useList(menuSnapshot);

    return useMemo(() => {
        if (!monthRanges.length) {
            return [];
        }
        const keys = menuKeys?.map((snap) => snap.key).filter(Boolean) as string[] ?? [];
        return monthRanges.map((range) => keys.some((key) => menuKeyInMonthRange(key, range)));
    }, [monthRanges, menuKeys]);
}

// Show the calendar month that contains today (includes early-month days before the first in-month Monday).
function getEffectiveMonthForMenu(currentDate: Date): { month: Date; isPreviousMonth: boolean } {
    return {
        month: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
        isPreviousMonth: false,
    };
}

function weekIndexForDay(weeks: string[][], dayStr: string): number {
    const idx = weeks.findIndex((week) => week.includes(dayStr));
    return idx >= 0 ? idx : 0;
}

function initialWeekIndexForMonth(weeks: string[][], targetDate: Date): number {
    const today = new Date();
    const sameMonth = today.getFullYear() === targetDate.getFullYear() && today.getMonth() === targetDate.getMonth();
    return sameMonth ? weekIndexForDay(weeks, toDayString(today)) : 0;
}

const monthTabButtonSx = (selected: boolean) => ({
    px: 2,
    py: 1.25,
    backgroundColor: selected ? '#D9F2F3' : 'transparent',
    color: selected ? '#01ABAE' : '#1D1A20',
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '1rem',
    borderRadius: 45,
    minWidth: 'auto',
    '&:hover': {
        backgroundColor: selected ? '#D9F2F3' : 'rgba(29, 26, 32, 0.1)',
    },
});

export default function MonthlyMenu() {
    const [lang] = useContext(langContext);
    const monthlyStrings = useMonthlyMenuStrings();
    const commonStrings = useStrings();
    const uid = auth.currentUser?.uid ?? null;
    const currentDate = useMemo(() => new Date(), []);
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const monthlyMenuExtraMonths = getMonthlyMenuExtraMonths(modelConstants);

    // Determine which month should be shown based on first Monday logic
    const effectiveMonth = useMemo(() => getEffectiveMonthForMenu(currentDate), [currentDate]);
    const monthDates = useMemo(
        () => buildMonthlyMenuMonthDates(effectiveMonth.month, monthlyMenuExtraMonths),
        [effectiveMonth.month, monthlyMenuExtraMonths]
    );
    const menusExistByMonth = useMenusExistForMonths(monthDates);

    const visibleMonthIndices = useMemo(
        () => monthDates.map((_, index) => index).filter((index) => menusExistByMonth[index]),
        [monthDates, menusExistByMonth]
    );

    const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        if (!visibleMonthIndices.length) {
            return;
        }
        if (!visibleMonthIndices.includes(selectedMonthIndex)) {
            setSelectedMonthIndex(visibleMonthIndices[0]);
        }
    }, [visibleMonthIndices, selectedMonthIndex]);

    // Check if user has seen the new menu notification
    // Use placeholder path when not signed in so hook always receives a valid ref
    const menuNtf: boolean = useDatabase(userNtfNewMenuPath(uid ?? '_'))?.val();

    // Show banner when user hasn't seen the menu yet (menuNtf is false)
    // Use a ref to capture the initial state before Navigator changes it
    const initialMenuNtf = useRef<boolean | null>(null);

    useEffect(() => {
        if (initialMenuNtf.current === null) {
            initialMenuNtf.current = menuNtf;
            if (!menuNtf) {
                setShowBanner(true);
            }
        }
    }, [menuNtf]);

    // Handle banner close
    const handleBannerClose = () => {
        setShowBanner(false);
    };

    const selectedMonthDate = monthDates[selectedMonthIndex] ?? effectiveMonth.month;

    const { dateStart, dateEnd } = useMemo(
        () => getCalendarMonthPrintDateRange(selectedMonthDate),
        [selectedMonthDate]
    );

    const menuData = usePublishedMenuDataForPrintMonth(menuPath(currentMenu), dateStart);
    const printSupportSnapshots = useMonthlyMenuPrintSupportSnapshots(currentSchoolKey);

    const [printErrorMsg, setPrintErrorMsg] = useState<string | null>(null);

    const onPrintMenu = useCallback(async () => {
        try {
            setPrintErrorMsg(null);
            await printMonthlyMenu({
                dateStart,
                dateEnd,
                menuData,
                ...monthlyMenuPrintSupportDataFromSnapshots(printSupportSnapshots),
                schoolKey: currentSchoolKey,
            });
        } catch (error) {
            if (error instanceof EmptyMonthlyMenuError) {
                setPrintErrorMsg(monthlyStrings.printMenuEmpty);
                return;
            }
            console.error('Failed to print monthly menu:', error);
            setPrintErrorMsg(
                error instanceof Error && error.message
                    ? error.message
                    : monthlyStrings.printMenuError
            );
        }
    }, [
        dateStart,
        dateEnd,
        menuData,
        printSupportSnapshots,
        monthlyStrings.printMenuEmpty,
        monthlyStrings.printMenuError,
    ]);

    return (
        <div className="packages-container">
            {/* New Menu Banner */}
            <NewMenuBanner
                isVisible={showBanner}
                onClose={handleBannerClose}
            />

            <Box sx={{
                display: 'flex',
                flexFlow: 'column',
                gap: 3,
                mb: 3,
                justifyContent: 'space-between',
                alignItems: 'stretch',
                '@media (min-width: 931px)': {
                    flexFlow: 'row',
                    alignItems: 'center',
                    gap: 1.5,
                },
            }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {visibleMonthIndices.map((monthIndex) => (
                        <Button
                            key={monthIndex}
                            variant="text"
                            onClick={() => setSelectedMonthIndex(monthIndex)}
                            sx={monthTabButtonSx(selectedMonthIndex === monthIndex)}
                        >
                            {monthlyStrings.monthName(monthDates[monthIndex].getMonth())}
                        </Button>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        aria-label={monthlyStrings.printMenu}
                        onClick={onPrintMenu}
                        startIcon={<PrintIcon style={{ width: 20, height: 20 }} />}
                        sx={{
                            borderRadius: '100px',
                            textTransform: 'none',
                            height: 40,
                            px: 2,
                            borderColor: 'var(--divider_white)',
                            color: 'var(--black_txt_title)',
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}
                    >
                        {monthlyStrings.printMenu}
                    </Button>
                    <AllergenPdfDownloadLink
                        lang={lang}
                        targetDate={selectedMonthDate}
                        isMonthlyMenu
                        label={monthlyStrings.allergensList}
                        className=""
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        imgStyle={{ marginRight: 0 }}
                        linkStyle={{ textDecoration: 'none', color: '#1D1A20' }}
                    />
                </Box>
            </Box>

            {visibleMonthIndices.length > 0 &&
                <WeeklySampleMenu targetDate={selectedMonthDate} />
            }

            {printErrorMsg && (
                <OkDlg
                    title={commonStrings.errorTitle}
                    msg={printErrorMsg}
                    onClose={() => setPrintErrorMsg(null)}
                />
            )}
        </div>
    );
}

// Week Navigator Component
function WeekNavigator({
    weekIndex,
    weekCount,
    onWeekChange,
    targetDate
}: {
    weekIndex: number;
    weekCount: number;
    onWeekChange: (weekIndex: number) => void;
    targetDate: Date;
}) {
    const monthlyStrings = useMonthlyMenuStrings();

    const weekNumber = weekIndex + 1;

    const monthName = monthlyStrings.monthName(targetDate.getMonth());
    const weekLabel = `${monthName} ${monthlyStrings.weekWord} ${weekNumber}`;

    const canGoPrevious = weekIndex > 0;
    const canGoNext = weekIndex < weekCount - 1;

    return (
        <Box
            data-week-navigator
            sx={{
                position: 'sticky',
                top: { xs: '60px', sm: 'calc(var(--header-height, 80px))' },
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                borderRadius: 2,
                mx: { xs: -2, sm: -2 },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
            }}>
            <button
                onClick={canGoPrevious ? () => onWeekChange(weekIndex - 1) : undefined}
                disabled={!canGoPrevious}
                style={{
                    flex: '0 0 36px',
                    borderRadius: '4px 0 0 4px',
                    minHeight: '74px',
                    border: 'none',
                    backgroundColor: 'var(--lb_green)',
                    color: 'white',
                    cursor: canGoPrevious ? 'pointer' : 'default'
                }}
            >
                <ChevronLeftIcon />
            </button>

            <Box sx={{
                flex: 1,
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '74px',
                px: 2
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#1D1A20',
                        fontWeight: 500,
                        fontSize: { xs: '13px', sm: '15px' },
                        textAlign: 'center',
                        fontFamily: 'Roboto'
                    }}
                >
                    {weekLabel}
                </Typography>
            </Box>

            <button
                onClick={canGoNext ? () => onWeekChange(weekIndex + 1) : undefined}
                disabled={!canGoNext}
                style={{
                    flex: '0 0 36px',
                    borderRadius: '0 4px 4px 0',
                    minHeight: '74px',
                    border: 'none',
                    backgroundColor: '#01ABAE',
                    color: 'white',
                    cursor: canGoNext ? 'pointer' : 'default'
                }}
            >
                <ChevronRightIcon />
            </button>
        </Box>
    );
}

function WeeklySampleMenu({ targetDate }: { targetDate: Date }) {
    const weeksInMonth = useMemo(
        () => getWeekArraysOverlappingCalendarMonth(targetDate.getFullYear(), targetDate.getMonth()),
        [targetDate]
    );

    const [weekIndex, setWeekIndex] = useState(() => initialWeekIndexForMonth(weeksInMonth, targetDate));

    useEffect(() => {
        setWeekIndex(initialWeekIndexForMonth(weeksInMonth, targetDate));
    }, [targetDate, weeksInMonth]);

    const currentWeek = weeksInMonth[weekIndex] ?? [];

    // Only scroll after explicit arrow navigation and only on small screens
    const [shouldScrollAfterChange, setShouldScrollAfterChange] = useState(false);

    const handleWeekChange = (newIndex: number) => {
        setWeekIndex(newIndex);
        if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
            setShouldScrollAfterChange(true);
        }
    };

    useEffect(() => {
        if (!shouldScrollAfterChange) return;
        requestAnimationFrame(() => {
            const el = document.getElementById('monday-anchor');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setShouldScrollAfterChange(false);
        });
    }, [weekIndex, shouldScrollAfterChange]);

    return (
        <Box sx={{ position: 'relative' }}>
            <WeekNavigator
                weekIndex={weekIndex}
                weekCount={weeksInMonth.length}
                onWeekChange={handleWeekChange}
                targetDate={targetDate}
            />
            <div className='sample-menu-container' style={{ margin: '0 0 24px' }}>
                {currentWeek.map((day, index) => <Menu1Day key={day} day={day} isWeekStart={index === 0} />)}
            </div>
        </Box>
    );
}

function Menu1Day({ day, isWeekStart }: { day: string; isWeekStart?: boolean }) {
    const [lang] = useContext(langContext);
    const monthlyStrings = useMonthlyMenuStrings();
    const setDayMenuRange = useSetDayMenuRangeForDate(day);
    const allowSetDayMenuExtras = !!setDayMenuRange;

    // Get course type texts from database
    const breakfastCourseText = useDatabase(`${COURSE_TYPES_PATH}/breakfast/${lang}`)?.val();
    const morningSnackCourseText = useDatabase(`${COURSE_TYPES_PATH}/morningSnack/${lang}`)?.val();

    const snapshot = useDatabase(`${menuPath(currentMenu)}/${day}`);
    const loading = !snapshot;
    const menuList = useList(snapshot);
    const hasMenuMeal = !!menuList.length;

    const dayNum = toDateFromServer(day).getDay();

    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const sideDish: Meal = useDatabase(`${menuPath(currentMenu)}/${day}/sideMenu`)?.val();
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const hasAnySideMenu = sideMenu || sideDay;
    const sideName: string = !sideMenu ? sideDay : sideMenu;

    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const dessert: Meal = useDatabase(`${menuPath(currentMenu)}/${day}/dessertMenu`)?.val();
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;

    return <div className="sample-menu-container-content">
        <div className='sample-menu-header-box' id={isWeekStart ? 'monday-anchor' : undefined} style={{ marginRight: '1px', borderRadius: isWeekStart ? '10px 0 0 0' : dayNum === 5 ? '0 10px 0 0' : '0' }}>
            <div className='sample-menu-header'>{getWeekdayName(dayNum, lang)}</div>
            <div className='sample-menu-header' style={{ fontWeight: 'normal' }}>{getDayAndMonth(toDateFromServer(day))}</div>
        </div>
        <div className='sample-menu-content-box' style={{ background: dayNum % 2 ? "rgba(1,171, 174, 0.1)" : "rgba(1,171, 174, 0.2)", borderRadius: isWeekStart ? '0 0 0 10px' : dayNum === 5 ? '0 0 10px 0' : '0' }}>
            {loading &&
                <div className='sample-menu-content' style={{ paddingTop: '40px' }}>{monthlyStrings.menuNotAvailable}</div>
                || <>
                    {
                        menuList
                            .filter((meal) => {
                                if (meal.key === 'dessertMenu' || meal.key === 'sideMenu') return false;
                                if (allowSetDayMenuExtras) return true;
                                const courseType = meal.val()?.courseType;
                                return courseType !== 'breakfast' && courseType !== 'morningSnack';
                            })
                            .sort((a, b) => {
                                const aVal = a.val();
                                const bVal = b.val();

                                // Define order priority: breakfast = 0, morningSnack = 1, others = 2
                                const getOrder = (courseType: string) => {
                                    if (courseType === 'breakfast') return 0;
                                    if (courseType === 'morningSnack') return 1;
                                    return 2;
                                };

                                return getOrder(aVal.courseType) - getOrder(bVal.courseType);
                            })
                            .map((meal: DataSnapshot<Meal>) => {
                                const mealVal = meal.val();
                                let menuTitle;
                                if (mealVal.isSide)
                                    menuTitle = monthlyStrings.sideDishCourseType;
                                else if (mealVal.isDessert)
                                    menuTitle = monthlyStrings.dessertCourseType;
                                else if (mealVal.courseType === 'breakfast')
                                    menuTitle = breakfastCourseText;
                                else if (mealVal.courseType === 'morningSnack')
                                    menuTitle = morningSnackCourseText;
                                else
                                    menuTitle = `${monthlyStrings.menuPrefix} ${mealVal.menuType}`;

                                const description = mealVal.description ? getLangValue(mealVal.description, lang) : null;
                                return <div key={meal.key} className='sample-menu-content'>
                                    <div style={{ fontWeight: 'bold' }}>{menuTitle}</div>
                                    <div>{getLangValue(mealVal.name, lang)}</div>
                                    {description && <div style={{ fontSize: '0.9em', color: '#555', marginTop: 2 }}>{description}</div>}
                                </div>
                            })
                    }
                    {!!hasAnySideMenu && hasMenuMeal &&
                        <div className='sample-menu-content'>
                            <div style={{ fontWeight: 'bold' }}>{monthlyStrings.sideDishHeading}</div>
                            <div>{sideName}</div>
                        </div>
                    }
                    {!!dessertName && hasMenuMeal &&
                        <div className='sample-menu-content'>
                            <div style={{ fontWeight: 'bold' }}>{monthlyStrings.dessertHeading}</div>
                            <div>{dessertName}</div>
                        </div>
                    }
                </>
            }
        </div>
    </div>
}